import { DateTime } from 'luxon';
import React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../cn';
import Button from '../Input2/Button';
import DateBoxButton from '../Input2/DateBoxButton';
import ButtonGroup from '../Input2/ButtonGroup';
import FilterSelect from '../Input2/FilterSelect';
import { StepIconSvg, type StepIconSvgType } from '../StepIcon';
import { General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import TeamUserAvatar from '../Avatar/TeamUserAvatar';

import type { ContactUser, GlobalReminder, GlobalReminderWithUser, TeamUserOption, ReminderActionData } from './types';

// ============================================================================
// ViewReminderActionCardPure
// ============================================================================

export type ViewReminderActionCardPureProps = {
  globalReminder: GlobalReminder & { planRunAt: Date | null; body?: string | null };
  globalReminderContactUser: ContactUser;
  currentUserId: string;
  teamUsers: TeamUserOption[];
  onChangeOwner: (newUserId: string) => Promise<void>;
  onClick?: () => void;
  deleting?: boolean;
  changingOwner?: boolean;
};

export function ViewReminderActionCardPure({
  globalReminder,
  globalReminderContactUser,
  currentUserId,
  teamUsers,
  onChangeOwner,
  onClick,
  deleting,
  changingOwner,
}: ViewReminderActionCardPureProps) {
  const dateTime = globalReminder.planRunAt ? DateTime.fromJSDate(globalReminder.planRunAt) : null;
  const isOwner = globalReminder.userId === currentUserId;

  const handleChangeOwner = async (userId: string) => {
    await onChangeOwner(userId);
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <ButtonGroup className="flex-1 min-w-0" color="blue" tone="soft">
        {dateTime && (
          <DateBoxButton
            readOnly
            month={dateTime.toFormat('LLL').toUpperCase()}
            day={dateTime.toFormat('d')}
          />
        )}
        <Button
          className="flex-1 min-w-0"
          tooltip={dateTime ? `Reminder: ${dateTime.toLocaleString(DateTime.DATE_MED)}` : undefined}
          onClick={!deleting && isOwner ? onClick : undefined}
          icon={dateTime ? undefined : <StepIconSvg iconType="reminder" className="text-zumthor-600" />}>
          <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{globalReminder.body || 'Reminder set'}</span>
        </Button>
        {!changingOwner && (
          <UserSelectDropdown
            users={teamUsers}
            selectedUserId={globalReminderContactUser.userId}
            currentUserId={currentUserId}
            onChange={handleChangeOwner}
            selectedUser={{
              profilePicture: globalReminderContactUser.profilePicture,
              userTeamName: globalReminderContactUser.name,
              userEmail: globalReminderContactUser.email,
            }}
          />
        )}
      </ButtonGroup>
      {changingOwner && <WaveSpinner className="text-zumthor-800" />}
    </div>
  );
}

// ============================================================================
// CadenceReminderActionCardPure
// ============================================================================

export type CadenceReminderActionCardPureProps = {
  globalReminder: GlobalReminder & { planRunAt?: Date | null; body?: string | null; cadenceStep: NonNullable<GlobalReminder['cadenceStep']> & { type: string } };
  onNavigateToInteract: () => void;
  handleOnDelete: () => void;
  deleting: boolean;
  /**
   * Injected dialer for a `call` step. Each surface (dashboard / extension) passes a function
   * that returns its own click-to-call button wired to this step id, so logging the call
   * outcome completes the step. Rendered only for `call`-type cadence reminders.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;
};

export function CadenceReminderActionCardPure({
  globalReminder,
  onNavigateToInteract,
  handleOnDelete,
  deleting,
  renderCadenceCallButton,
}: CadenceReminderActionCardPureProps) {
  const dateTime = globalReminder?.planRunAt ? DateTime.fromJSDate(globalReminder.planRunAt) : undefined;
  const stepName = formatStepType(globalReminder.cadenceStep.type);
  const isCall = globalReminder.cadenceStep.type === 'call';

  return (
    <div className="group flex min-w-0 gap-1.5">
      <ButtonGroup className="flex-1 min-w-0" color="blue" tone="soft">
        {dateTime && (
          <DateBoxButton
            readOnly
            month={dateTime.toFormat('LLL').toUpperCase()}
            day={dateTime.toFormat('d')}
          />
        )}
        <Button
          className="flex-1 min-w-0"
          tooltip={dateTime ? `Reminder: ${dateTime.toLocaleString(DateTime.DATE_MED)}` : undefined}
          onClick={onNavigateToInteract}
          icon={dateTime ? undefined : <StepIconSvg iconType={globalReminder.cadenceStep.type as StepIconSvgType} className="text-zumthor-600" />}>
          <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{globalReminder?.body || stepName}</span>
        </Button>
      </ButtonGroup>
      {isCall && renderCadenceCallButton?.({ cadenceStepId: globalReminder.cadenceStep.stepId })}
      {deleting && <WaveSpinner size={20} />}
      {!deleting && (
        <Button
          color="red"
          icon={<General.Trash01 />}
          type="reset"
          onClick={(e) => {
            e.stopPropagation();
            handleOnDelete();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// CreateEditReminderActionPure
// ============================================================================

export type CreateEditReminderActionPureProps = {
  editingGlobalReminder?: GlobalReminder & { planRunAt?: Date | null; body?: string | null };
  defaultReminderName?: string;
  reminderOptions: { value: string; label: string }[];
  onSubmit: (planRunAt: number, body?: string) => void;
  loading?: boolean;
  onCancel?: () => void;
  deleting?: boolean;
  onClickDelete?: () => void;
  /** Create a new reusable reminder type from the type dropdown, then select it. */
  onCreateReminderType?: (name: string) => Promise<void>;
  /** Whether a reminder type is currently being created. */
  creatingReminderType?: boolean;
};

type DateOption = { value: number; label: string };

function getInitialDates(): DateOption[] {
  const todaysDate = new Date();
  const tomDateTime = DateTime.fromJSDate(todaysDate).plus({ days: 1 }).endOf('day').toMillis();
  const weekDateTime = DateTime.fromJSDate(todaysDate).plus({ week: 1 }).endOf('day').toMillis();
  const ThirtyDateTime = DateTime.fromJSDate(todaysDate).plus({ month: 1 }).endOf('day').toMillis();
  const NinetyDateTime = DateTime.fromJSDate(todaysDate).plus({ month: 3 }).endOf('day').toMillis();

  return [
    { value: tomDateTime, label: 'tomorrow' },
    { value: weekDateTime, label: 'a week from now' },
    { value: ThirtyDateTime, label: 'a month from now' },
    { value: NinetyDateTime, label: '3 months from now' },
  ];
}

const customDateLabel = 'a custom day';

function getSelectedDate(defaultDate?: number): DateOption | undefined {
  if (!defaultDate) return undefined;
  const dates = getInitialDates();
  return dates.find((date) => date.value === defaultDate);
}

function getPickedDate(defaultDate?: number): DateOption | undefined {
  if (!defaultDate) return undefined;
  const dates = getInitialDates();
  if (dates.every((date) => date.value !== defaultDate)) {
    return { value: defaultDate, label: customDateLabel };
  }
  return undefined;
}

export function CreateEditReminderActionPure({
  editingGlobalReminder,
  defaultReminderName,
  reminderOptions,
  onSubmit,
  loading,
  onCancel,
  deleting,
  onClickDelete,
  onCreateReminderType,
  creatingReminderType,
}: CreateEditReminderActionPureProps) {
  const defaultDate = editingGlobalReminder?.planRunAt
    ? DateTime.fromJSDate(editingGlobalReminder.planRunAt).toMillis()
    : undefined;

  const [reminderText, setReminderText] = React.useState<string>(
    editingGlobalReminder?.body || defaultReminderName || '',
  );
  const [selectedDate, setSelectedDate] = React.useState<DateOption | undefined>(getSelectedDate(defaultDate));
  const [pickedDate, setPickedDate] = React.useState<DateOption | undefined>(getPickedDate(defaultDate));
  const [wasChanged, setWasChanged] = React.useState(false);
  const dates = React.useMemo(() => getInitialDates(), []);

  const onReminderSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (loading) return;

    const reminderDate = selectedDate || pickedDate;
    if (!reminderDate) return;

    onSubmit(reminderDate.value, reminderText);
  };

  React.useEffect(() => {
    if (reminderText && (selectedDate || pickedDate) && wasChanged) {
      onReminderSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderText, selectedDate, pickedDate, wasChanged]);

  return (
    <form
      className={cn(
        'relative flex flex-col rounded-lg border border-astronaut-100 dark:border-astronaut-900 overflow-hidden',
      )}
      onSubmit={onReminderSubmit}>
      <div className="flex gap-1.5 p-1">
        <ButtonGroup className="flex-1 min-w-0" color="blue" tone="soft">
          <Button className="flex-1" icon={<General.CheckSquareBroken />}>
            <span>
              Set a <u>Reminder</u>
            </span>
          </Button>
          {editingGlobalReminder && (
            <Button
              type="reset"
              color="red"
              onClick={onClickDelete}
              icon={deleting ? <WaveSpinner /> : <General.Trash01 />}
            />
          )}
        </ButtonGroup>
        {onCancel && (
          <Button color="blue" tone="soft" tooltip="Dismiss" onClick={onCancel} icon={<General.XMark className="size-4" />} />
        )}
      </div>

      <div className="flex items-center gap-1.5 px-3 py-2">
        {dates.map((date, i) => {
          // Default state (no selection at all): all preset tiles look "off". Hovering previews
          // the active styling, clicking locks it in. Once a tile is selected the unselected
          // tiles stay faded.
          const faded = !selectedDate || selectedDate.value !== date.value;
          return (
            <DateBoxButton
              key={i}
              size="xl"
              month={DateTime.fromMillis(date.value).toFormat('LLL').toUpperCase()}
              day={DateTime.fromMillis(date.value).toFormat('d')}
              faded={faded}
              onClick={() => {
                setSelectedDate(date);
                setWasChanged(true);
                setPickedDate(undefined);
              }}
            />
          );
        })}
        <CustomDatePicker
          value={pickedDate}
          onChange={(date) => {
            setPickedDate(date);
            setSelectedDate(undefined);
            setWasChanged(true);
          }}
          faded={pickedDate == null}
        />
      </div>

      <div className="flex min-h-9 w-full flex-col border-t border-astronaut-100 dark:border-astronaut-900">
        <ReminderTextSelect
          value={reminderText}
          onChange={(value) => {
            setReminderText(value);
            setWasChanged(true);
          }}
          options={reminderOptions}
          onCreate={onCreateReminderType}
          creating={creatingReminderType}
        />
      </div>
    </form>
  );
}

// ============================================================================
// ReminderActionCardPure - Main Component
// ============================================================================

export type ReminderActionCardPureProps = {
  // Flattened from contactUser - only field actually used
  currentUserId: string;

  // Data that was previously fetched internally
  cadenceReminders: Array<{
    globalReminder: GlobalReminder & { planRunAt?: Date | null; body?: string | null; cadenceStep: NonNullable<GlobalReminder['cadenceStep']> & { type: string } };
  }>;
  nonCadenceReminders: GlobalReminderWithUser[];
  defaultReminderAction?: ReminderActionData;
  reminderOptions: { value: string; label: string }[];
  teamUsers: TeamUserOption[];
  isLoading: boolean;

  // Mutation callbacks
  onCreateReminder: (planRunAt: number, body?: string) => Promise<void>;
  onUpdateReminder: (id: string, planRunAt: number, body?: string) => Promise<void>;
  onDeleteReminder: (id: string) => Promise<void>;
  onChangeOwner: (reminderId: string, newUserId: string) => Promise<void>;
  onNavigateToInteract: () => void;
  /** Persist a new reusable reminder type from the type dropdown, then select it. */
  onCreateReminderAction?: (name: string) => Promise<void>;
  /** Injected dialer for `call`-type cadence reminders (see CadenceReminderActionCardPure). */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;

  // Loading states
  deletingIds: Set<string>;
  createPending: boolean;
  updatePending: boolean;
  changeOwnerPending: boolean;
  /** Whether a reminder type is currently being created. */
  creatingReminderAction?: boolean;
};

export function ReminderActionCardPure({
  currentUserId,
  cadenceReminders,
  nonCadenceReminders,
  defaultReminderAction,
  reminderOptions,
  teamUsers,
  isLoading,
  onCreateReminder,
  onUpdateReminder,
  onDeleteReminder,
  onChangeOwner,
  onNavigateToInteract,
  onCreateReminderAction,
  renderCadenceCallButton,
  deletingIds,
  createPending,
  updatePending,
  changeOwnerPending,
  creatingReminderAction,
}: ReminderActionCardPureProps) {
  const [editing, setEditing] = React.useState<GlobalReminder & { planRunAt?: Date | null; body?: string | null }>();
  const [creating, setCreating] = React.useState(false);

  const nonCadenceSelfReminder = nonCadenceReminders.find(
    (g) => g.globalReminder.userId === currentUserId,
  );

  const handleDelete = async (id: string) => {
    await onDeleteReminder(id);
    setEditing(undefined);
  };

  const handleCreate = async (planRunAt: number, body?: string) => {
    await onCreateReminder(planRunAt, body);
    setCreating(false);
  };

  const handleUpdate = async (planRunAt: number, body?: string) => {
    if (!editing) return;
    await onUpdateReminder(editing.id, planRunAt, body);
    setEditing(undefined);
  };

  if (isLoading) {
    return <LoadingReminderCard />;
  }

  return (
    <>
      {cadenceReminders.map((cadenceReminder, idx) => (
        <CadenceReminderActionCardPure
          key={cadenceReminder.globalReminder.id ?? idx}
          globalReminder={cadenceReminder.globalReminder}
          onNavigateToInteract={onNavigateToInteract}
          deleting={deletingIds.has(cadenceReminder.globalReminder.id)}
          handleOnDelete={() => onDeleteReminder(cadenceReminder.globalReminder.id)}
          renderCadenceCallButton={renderCadenceCallButton}
        />
      ))}

      {nonCadenceReminders.map((reminder) => {
        if (reminder.globalReminder.id === editing?.id) {
          return (
            <CreateEditReminderActionPure
              key={reminder.globalReminder.id}
              editingGlobalReminder={editing}
              defaultReminderName={defaultReminderAction?.name ?? undefined}
              reminderOptions={reminderOptions}
              onSubmit={handleUpdate}
              loading={updatePending}
              onCancel={() => setEditing(undefined)}
              deleting={deletingIds.has(editing.id)}
              onClickDelete={() => handleDelete(editing.id)}
              onCreateReminderType={onCreateReminderAction}
              creatingReminderType={creatingReminderAction}
            />
          );
        }

        return (
          <ViewReminderActionCardPure
            key={reminder.globalReminder.id}
            globalReminder={reminder.globalReminder}
            globalReminderContactUser={reminder.globalReminderContactUser}
            currentUserId={currentUserId}
            teamUsers={teamUsers}
            onChangeOwner={(newUserId) => onChangeOwner(reminder.globalReminder.id, newUserId)}
            onClick={() => setEditing(reminder.globalReminder)}
            deleting={deletingIds.has(reminder.globalReminder.id)}
            changingOwner={changeOwnerPending}
          />
        );
      })}

      {nonCadenceSelfReminder == null && !creating && (
        <Button color="blue" tone="soft" icon={<StepIconSvg iconType="reminder" />} onClick={() => setCreating(true)}>
          <span>
            Set a <u>Reminder</u>
          </span>
        </Button>
      )}
      {nonCadenceSelfReminder == null && creating && (
        <CreateEditReminderActionPure
          defaultReminderName={defaultReminderAction?.name ?? undefined}
          reminderOptions={reminderOptions}
          onSubmit={handleCreate}
          loading={createPending}
          deleting={false}
          onCancel={() => setCreating(false)}
          onCreateReminderType={onCreateReminderAction}
          creatingReminderType={creatingReminderAction}
        />
      )}
    </>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function LoadingReminderCard() {
  return (
    <Button color="blue" tone="soft" icon={<WaveSpinner className="text-zumthor-800" />}>
      Loading...
    </Button>
  );
}

type CustomDatePickerProps = {
  value?: DateOption;
  onChange: (date: DateOption) => void;
  faded?: boolean;
};

function CustomDatePicker({ value, onChange, faded }: CustomDatePickerProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const displayDate = value ? DateTime.fromMillis(value.value) : null;

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    if (!dateValue) return;
    const dt = DateTime.fromISO(dateValue).endOf('day');
    onChange({ value: dt.toMillis(), label: customDateLabel });
  };

  return (
    <DateBoxButton
      size="xl"
      month={displayDate ? displayDate.toFormat('LLL').toUpperCase() : 'PICK'}
      day={displayDate ? displayDate.toFormat('d') : '?'}
      faded={!displayDate || faded}
      onClick={() => inputRef.current?.showPicker()}>
      <input
        ref={inputRef}
        type="date"
        className="absolute inset-0 opacity-0 cursor-pointer"
        min={DateTime.now().plus({ days: 1 }).toISODate() ?? undefined}
        onChange={handleDateChange}
      />
    </DateBoxButton>
  );
}

type UserSelectDropdownProps = {
  users: TeamUserOption[];
  selectedUserId: string;
  currentUserId: string;
  onChange: (userId: string) => void;
  selectedUser: {
    profilePicture: string | null;
    userTeamName: string | null;
    userEmail: string | null;
  };
  /** ButtonGroup passes className via cloneElement; forwarded to the wrapper. */
  className?: string;
};

function UserSelectDropdown({ users, selectedUserId, currentUserId, onChange, selectedUser, className }: UserSelectDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  // Anchor the menu to the trigger via fixed positioning + portal so ancestor
  // overflow:hidden / clip-path can't crop the dropdown.
  const [menuPos, setMenuPos] = React.useState<{ top: number; right: number } | null>(null);

  React.useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, [open]);

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="flex h-full items-center px-2 hover:bg-astronaut-200/50 dark:hover:bg-astronaut-800/50">
        <TeamUserAvatar
          userTeam={{
            profilePicture: selectedUser.profilePicture,
            userTeamName: selectedUser.userTeamName,
            userEmail: selectedUser.userEmail,
          }}
          className="size-6"
          shape="circle"
        />
      </button>
      {open && menuPos != null && typeof document !== 'undefined' && createPortal(
        <>
          <div className="fixed inset-0 z-60" onClick={() => setOpen(false)} />
          <div
            className="fixed z-61 max-h-48 min-w-40 overflow-auto rounded-lg border border-astronaut-200 bg-white shadow-lg dark:border-astronaut-700 dark:bg-astronaut-900"
            style={{ top: menuPos.top, right: menuPos.right }}>
            {users.map((user) => {
              const isMe = user.userId === currentUserId;
              const isSelected = user.userId === selectedUserId;
              return (
                <button
                  key={user.userId}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(user.userId);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-astronaut-100 dark:hover:bg-astronaut-800',
                    isSelected && 'bg-astronaut-50 dark:bg-astronaut-800',
                  )}>
                  <TeamUserAvatar
                    userTeam={{
                      profilePicture: user.profilePicture,
                      userTeamName: user.userTeamName,
                      userEmail: user.userEmail,
                    }}
                    className="size-5"
                    shape="circle"
                  />
                  <span className="truncate">
                    {user.userTeamName ?? user.userEmail}
                    {isMe && ' (Me)'}
                  </span>
                </button>
              );
            })}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

type ReminderTextSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  /**
   * When provided, typing a name that doesn't match a preset shows a
   * "Create X" row that persists a new reusable reminder type and then
   * selects it as this reminder's body.
   */
  onCreate?: (name: string) => Promise<void>;
  /** Whether a reminder type is currently being created (shows a spinner). */
  creating?: boolean;
};

function ReminderTextSelect({ value, onChange, options, onCreate, creating }: ReminderTextSelectProps) {
  // Surface the current value even when it isn't (yet) one of the preset
  // options — e.g. a freshly-created reminder type that hasn't round-tripped
  // through the server, or an existing reminder with a custom body. Without
  // this the trigger falls back to the placeholder and the chosen text
  // silently disappears.
  const optionsWithValue =
    value && !options.some((o) => o.value === value) ? [...options, { value, label: value }] : options;

  const handleCreate = (name: string) => {
    // If the typed name already matches a preset, select it instead of
    // creating a duplicate reminder type.
    const existing = options.find((o) => o.value.trim().toLowerCase() === name.trim().toLowerCase());
    if (existing) {
      onChange(existing.value);
      return;
    }
    // Optimistically select the typed text as the body so the form is usable
    // immediately even if persistence is slow or fails, then persist the
    // reusable reminder type.
    onChange(name);
    void onCreate?.(name);
  };

  return (
    <FilterSelect
      value={value}
      onChange={onChange}
      options={optionsWithValue}
      placeholder="Select reminder type..."
      bordered={false}
      // Force the filter input on when creation is allowed so the user can
      // always type a new name, regardless of how many presets exist.
      filterable={!!onCreate || options.length > 5}
      status={creating ? 'updating' : undefined}
      onCreate={onCreate ? handleCreate : undefined}
      className="w-full"
    />
  );
}

function formatStepType(type: string): string {
  const typeMap: Record<string, string> = {
    message: 'Message',
    connect: 'Connect',
    inmail: 'InMail',
    visit: 'Visit Profile',
    follow: 'Follow',
    email: 'Email',
    reminder: 'Reminder',
    call: 'Call',
    delay: 'Delay',
    likeRecentPosts: 'Like Posts',
    wait_for_connect: 'Wait for Connect',
    voice_note: 'Voice Note',
  };
  return typeMap[type] || type;
}
